import { useRef, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { AiOutlineCheck } from 'react-icons/ai';
import TextInput from 'components/TextInput';
import { colorOptions, colors } from '../../../../constants';
import ColorTag from '../ColorTag';
import { useOutsideClick } from 'hooks/useOutsideClick';

const StyledSelectColorContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const SelectColorContainer = styled.div`
  position: absolute;
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  width: 332px;
  max-height: 160px;
  overflow-y: auto;
  position: absolute;
  z-index: 2;
  ${props => props.direction}: 40px;
  right: 0;
  box-shadow: 0px 6px 12px 0px ${colors.navy}12;
`;

const TextInputContainer = styled.div`
  padding: 10px;
  input {
    width: 100%;
  }
`;

const ColorOption = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  cursor: pointer;
`;

const SelectColor = ({
  onChangeRow,
  data,
  children,
  show,
  direction,
  onOutsideClick,
}) => {
  const { t } = useTranslation();
  const selectColorContainerRef = useRef();

  const [searchText, setSearchText] = useState('');
  const [filteredColorOptions, setFilteredColorOptions] = useState(
    colorOptions(t),
  );

  useOutsideClick(selectColorContainerRef, onOutsideClick, show);

  const handleClick = colorOption => () => {
    const isAlreadyChosen = data.color.find(
      item => item.value === colorOption.value,
    );
    if (isAlreadyChosen) {
      const filteredColors = data.color.filter(
        item => item.value !== colorOption.value,
      );
      onChangeRow({ ...data, color: filteredColors });
    } else {
      onChangeRow({ ...data, color: [...data.color, colorOption] });
    }
  };

  const handleChangeSearch = event => {
    setSearchText(event.target.value);
    const filteredColorOptions = colorOptions(t).filter(item =>
      item.label.match(new RegExp(event.target.value, 'gi')),
    );
    setFilteredColorOptions(filteredColorOptions);
  };

  return (
    <StyledSelectColorContainer ref={selectColorContainerRef}>
      {children}
      {show && (
        <SelectColorContainer direction={direction}>
          <TextInputContainer>
            <TextInput
              type="text"
              placeholder={t('select.color.input.placeholder')}
              value={searchText}
              onChange={handleChangeSearch}
            />
          </TextInputContainer>
          {filteredColorOptions.map(item => {
            const isChosen = data.color.find(
              color => color.value === item.value,
            );
            return (
              <ColorOption key={item.value} onClick={handleClick(item)}>
                <ColorTag
                  color={item.color}
                  labelColor={item.labelColor}
                  label={item.label}
                />
                {isChosen && <AiOutlineCheck fill={colors.green} />}
              </ColorOption>
            );
          })}
        </SelectColorContainer>
      )}
    </StyledSelectColorContainer>
  );
};

SelectColor.propTypes = {
  data: PropTypes.object.isRequired,
  onChangeRow: PropTypes.func.isRequired,
  onOutsideClick: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  direction: PropTypes.oneOf(['top', 'bottom']),
};

export default SelectColor;
